import { Link, useNavigate, useParams } from "react-router-dom";
import { Building2, BadgeEuro, ChevronRight, ChevronLeft, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBreadcrumb } from "@/components/layout/BreadcrumbContext";
import { BienDetailPage } from "@/pages/biens/BienDetailPage";
import { BienVenteDetailPage } from "@/v2/pages/BienVenteDetailPage";
import { useBien } from "@/hooks/useBiens";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { isForSale } from "@/lib/transaction";
import { useRole } from "@/contexts/RoleContext";
import { cn } from "@/lib/utils";

/**
 * Wrapper V2 autour de la fiche bien.
 *
 * - Affiche le fil d'Ariane en haut (BIENS > référence [transaction] < x/y >)
 *   en lisant le BreadcrumbContext alimenté par useBreadcrumbDetail() dans
 *   la page montée.
 * - Ajoute du padding latéral / vertical autour du formulaire pour aérer
 *   le rendu (V1 s'appuyait sur le padding du <main> qui n'existe pas
 *   en V2 où chaque page gère son padding).
 * - BIFURCATION VENTE (chantier Catalogue Sales 2026-07-12) : un bien
 *   `transaction === "vente"` est rendu par BienVenteDetailPage (sections de
 *   la fiche vente, config venteFormFields) ; tout le reste garde la fiche
 *   V1 BienDetailPage (location) telle quelle — validation, bannières,
 *   autosave et permissions intactes.
 */
export function V2BienDetailWrapper() {
  const { id } = useParams<{ id: string }>();
  const permRole = useBiensPermRole();
  // Même query key que la page montée ensuite → un seul fetch (cache partagé).
  const { data: bien, isLoading } = useBien(id, permRole);
  const forSale = isForSale(bien?.transaction);
  return (
    <div className="p-4 md:p-6">
      <V2Breadcrumb forSale={forSale} />
      {/* Tant que la transaction est inconnue, ne monter AUCUNE des deux
          fiches (évite un flash location → vente au premier chargement). */}
      {isLoading && !bien ? null : forSale ? <BienVenteDetailPage /> : <BienDetailPage />}
    </div>
  );
}

function V2Breadcrumb({ forSale }: { forSale: boolean }) {
  const { detail } = useBreadcrumb();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { role } = useRole();
  const { t } = useTranslation();
  // Racine du fil d'Ariane : catalogue vente pour un bien vente, catalogue
  // location sinon (chantier Catalogue Sales 2026-07-12).
  const catalogueTo = forSale ? "/v2/biens-vente" : "/v2/biens";
  const catalogueLabel = t(forSale ? "v2.nav.catalogueVente" : "v2.nav.catalogueLocation");
  const CatalogueIcon = forSale ? BadgeEuro : Building2;
  // Duplication : C-Level et admin, + équipe Sales sur ses biens vente
  // (la modale Location/Vente reste le seul endroit où la transaction du
  // duplicata se choisit).
  const canDuplicate = role === "c_level" || role === "admin" || (role === "vente" && forSale);
  if (!detail) {
    // Fallback : juste le catalogue tant que useBreadcrumbDetail n'a pas hydraté
    return (
      <div className="flex items-center gap-2 text-text-muted text-sm mb-4">
        <Link to={catalogueTo} className="inline-flex items-center gap-2 hover:text-text-primary transition-colors">
          <CatalogueIcon size={16} />
          <span className="font-semibold uppercase tracking-wider text-xs">{catalogueLabel}</span>
        </Link>
      </div>
    );
  }

  const nav = detail.navigation;

  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      <Link
        to={catalogueTo}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors"
      >
        <CatalogueIcon size={16} />
        <span className="font-semibold uppercase tracking-wider text-xs">{catalogueLabel}</span>
      </Link>
      <ChevronRight size={14} className="text-text-muted" />
      <span className="text-sm font-semibold text-text-primary uppercase tracking-wider">
        {detail.label}
      </span>
      {detail.badge}

      {nav && nav.total > 1 && (
        <div className="flex items-center gap-1 ml-2 select-none">
          <button
            type="button"
            onClick={() => nav.prevPath && navigate(nav.prevPath.replace(/^\/biens/, "/v2/biens"))}
            disabled={!nav.prevPath}
            className={cn(
              "p-1 rounded text-text-muted hover:text-accent hover:bg-gray-100 transition-colors cursor-pointer",
              !nav.prevPath && "opacity-30 cursor-not-allowed",
            )}
            aria-label={t("v2.bienDetail.shell.breadcrumbPrev")}
            title={t("v2.bienDetail.shell.breadcrumbPrev")}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="text-[11px] font-medium text-text-muted tabular-nums">
            {nav.position}/{nav.total}
          </span>
          <button
            type="button"
            onClick={() => nav.nextPath && navigate(nav.nextPath.replace(/^\/biens/, "/v2/biens"))}
            disabled={!nav.nextPath}
            className={cn(
              "p-1 rounded text-text-muted hover:text-accent hover:bg-gray-100 transition-colors cursor-pointer",
              !nav.nextPath && "opacity-30 cursor-not-allowed",
            )}
            aria-label={t("v2.bienDetail.shell.breadcrumbNext")}
            title={t("v2.bienDetail.shell.breadcrumbNext")}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        {canDuplicate && (
          <button
            type="button"
            onClick={() => id && navigate("/v2/biens/new", { state: { duplicateFromId: id } })}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:text-accent hover:border-accent border border-border rounded-md cursor-pointer whitespace-nowrap transition-colors"
            title={t("v2.bienDetail.duplicateTitle", "Dupliquer cette fiche (nouvel identifiant à saisir)")}
          >
            <Copy size={13} /> {t("v2.bienDetail.duplicate", "Dupliquer")}
          </button>
        )}
        {detail.actions}
      </div>
    </div>
  );
}
